// src/store/appStore.js
import { create } from 'zustand';

// Create application state store
export const useAppStore = create((set, get) => ({
  // Video state
  videoState: {
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    isBuffering: false,
    playbackRate: 1,
    volume: 1,
    lastVideoSwitch: null,
    isHls: false,
    hlsResumeTime: null,
    time: 0,
    hlsBaseTime: 0
  },
  
  
  // Selection-related state
  selection: {
    activeRobotId: null, // Currently active robot ID
    activeEventId: null, // Currently active event ID
    selectedVideos: {}, // Selected videos {id: boolean}
  },
  
  // Timeframe related
  timeframe: "11AM",
  
  // Video source
  videoUrl: "",
  
  // Metadata
  metadata: {
    lastUpdate: Date.now(),
    updateSource: null,
    processingUpdate: false,
  },
  
  // Tracked objects state
  trackedObjects: {
    person: [],
    vehicle: [],
    event: []
  },
  
  // Action: Update video state
// Key fix for appStore.js: Optimized updateVideoState function to eliminate feedback loops

// Replace or update the existing implementation with this one
updateVideoState: (updates, source = null) => {
  // 添加时间范围检查
  if (updates.time !== undefined) {
    // 限制时间在合理范围内（30分钟 = 1800秒）
    const MAX_VIDEO_DURATION = 1800;
    updates.time = Math.min(Math.max(0, updates.time), MAX_VIDEO_DURATION);
  }

  if (source === 'videoPlayer_hls_sync' && get().videoState.isHls) {
    set(state => ({
      videoState: {
        ...state.videoState,
        currentTime: Math.min(updates.time + state.videoState.hlsBaseTime, 1800) // 限制最大时间
      }
    }));
    return;
  }
  

  const currentState = get();
  const { metadata } = currentState;
  const timestamp = new Date().toISOString();
  
  // Always explicitly clear the video switch flag if it's not needed
  // This is key to preventing the recurring cycles
  if (!updates.lastVideoSwitch && updates.lastVideoSwitch !== null && currentState.videoState.lastVideoSwitch) {
    updates = { ...updates, lastVideoSwitch: null };
    console.log(`[AppStore ${timestamp}] 🧹 自动清除未使用的视频切换标志`);
  }
  
  // Special handling for timeline event clicks
  if (source === 'timeline_event_click') {
    console.log(`[AppStore ${timestamp}] 🔄 开始处理事件点击, 当前锁状态:`, window.timelineEventClickInProgress);
    
    // Check for a stuck lock (over 5 seconds)
    if (window.timelineEventClickInProgress && 
        window.timelineEventClickStartTime && 
        (Date.now() - window.timelineEventClickStartTime > 5000)) {
      console.warn(`[AppStore ${timestamp}] ⚠️ 检测到卡住的事件锁，已持续${(Date.now() - window.timelineEventClickStartTime)/1000}秒，强制释放`);
      window.timelineEventClickInProgress = false;
      window.timelineEventClickStartTime = null;
      
      if (window.timelineAutoplayTimer) {
        clearTimeout(window.timelineAutoplayTimer);
        window.timelineAutoplayTimer = null;
      }
    }
    
    // Prevent duplicate processing
    if (window.timelineEventClickInProgress) {
      console.log(`[AppStore ${timestamp}] ⏭️ 忽略重复的timeline_event_click，上一个处理仍在进行中`);
      return;
    }
    
    // Set processing flag and record start time
    window.timelineEventClickInProgress = true;
    window.timelineEventClickStartTime = Date.now();
    window.isVideoChanging = true; // Additional flag to prevent invalid pause events
    
    console.log(`[AppStore ${timestamp}] 📊 处理timeline事件点击更新:`, updates);
    
    // 先保存HLS相关属性，确保不会丢失
    const hlsProps = {
      isHls: updates.isHls,
      hlsResumeTime: updates.hlsResumeTime,
      hlsBaseTime: updates.hlsBaseTime,
      currentEvent: updates.currentEvent
    };
    
    // Check if video switching is needed
    const isVideoSwitch = 
      (updates.videoUrl && updates.videoUrl !== currentState.videoUrl) || 
      (updates.robotId && updates.robotId !== currentState.selection.activeRobotId);
    
    // 执行一次完整的批量更新，避免多次更新导致状态丢失
    set(state => {
      const newState = { ...state };
      
      // 更新视频URL和机器人ID
      if (updates.videoUrl) {
        newState.videoUrl = updates.videoUrl;
      }
      
      if (updates.robotId !== undefined) {
        newState.selection = { ...state.selection, activeRobotId: updates.robotId };
      }
      
      if (updates.eventId !== undefined) {
        newState.selection = { ...newState.selection, activeEventId: updates.eventId };
      }
      
      // 更新视频状态
      newState.videoState = { ...state.videoState };
      
      // 设置时间相关属性
      if (updates.time !== undefined) {
        newState.videoState.currentTime = updates.time;
        newState.videoState.time = updates.time; // 设置偏移时间
      }
      
      if (updates.isPlaying !== undefined) {
        newState.videoState.isPlaying = updates.isPlaying;
      }
      
      // 设置视频切换标志
      if (isVideoSwitch) {
        console.log(`[AppStore ${timestamp}] 🔄 设置视频切换标志（一次性）`);
        newState.videoState.lastVideoSwitch = Date.now();
      } else {
        newState.videoState.lastVideoSwitch = null;
      }
      
      // 设置HLS相关属性
      if (hlsProps.isHls !== undefined) {
        newState.videoState.isHls = hlsProps.isHls;
        console.log(`[AppStore ${timestamp}] 🎬 设置 isHls: ${hlsProps.isHls}`);
      }
      
      if (hlsProps.hlsResumeTime !== undefined) {
        newState.videoState.hlsResumeTime = hlsProps.hlsResumeTime;
        console.log(`[AppStore ${timestamp}] ⏱️ 设置 hlsResumeTime: ${hlsProps.hlsResumeTime}`);
      }
      
      if (hlsProps.hlsBaseTime !== undefined) {
        newState.videoState.hlsBaseTime = hlsProps.hlsBaseTime;
        console.log(`[AppStore ${timestamp}] 🏁 设置 hlsBaseTime: ${hlsProps.hlsBaseTime}`);
      }
      
      if (hlsProps.currentEvent !== undefined) {
        newState.videoState.currentEvent = hlsProps.currentEvent;
      }
      
      // 更新元数据
      newState.metadata = {
        lastUpdate: Date.now(),
        updateSource: source,
        processingUpdate: isVideoSwitch,
      };
      
      return newState;
    });
    
    // Clear any existing play timers
    if (window.timelineAutoplayTimer) {
      clearTimeout(window.timelineAutoplayTimer);
      window.timelineAutoplayTimer = null;
    }
    
    // If switching video and currently paused, delay autoplay
    if (isVideoSwitch && !updates.isPlaying) {
      // Create a single autoplay timer - reduce delay from 300ms to 100ms
      console.log(`[AppStore ${timestamp}] ⏱️ 设置视频切换后自动播放定时器: 100ms`);
      window.timelineAutoplayTimer = setTimeout(() => {
        // Clear timer reference
        window.timelineAutoplayTimer = null;
        
        // Reset processing flag
        console.log(`[AppStore ${timestamp}] ✅ 视频切换后自动播放触发，释放锁`);
        window.timelineEventClickInProgress = false;
        window.timelineEventClickStartTime = null;
        window.isVideoChanging = false;
        
        // Update playback state
        set(state => ({
          videoState: {
            ...state.videoState,
            isPlaying: true,
            lastVideoSwitch: null // Explicitly clear flag
          },
          metadata: {
            ...state.metadata,
            processingUpdate: false,
            updateSource: null,
          }
        }));
        console.log(`[AppStore ${timestamp}] ▶️ 自动播放已启动`);
      }, 100);
    } else {
      // Fast reset of processing flag
      setTimeout(() => {
        console.log(`[AppStore ${timestamp}] ✅ 快速释放timeline事件锁`);
        window.timelineEventClickInProgress = false;
        window.timelineEventClickStartTime = null;
        window.isVideoChanging = false; 
        
        // 确保在释放锁后重新检查并设置HLS状态
        const currentState = get();
        const videoUrl = currentState.videoUrl;
        const isHls = videoUrl && videoUrl.includes('.m3u8');
        
        if (isHls && !currentState.videoState.isHls) {
          console.log(`[AppStore ${timestamp}] 🔄 最终检查：检测到HLS视频但状态不正确，修正状态`);
          set(state => ({
            videoState: {
              ...state.videoState,
              isHls: true,
              hlsBaseTime: hlsProps.hlsBaseTime !== undefined ? hlsProps.hlsBaseTime : state.videoState.hlsBaseTime,
              hlsResumeTime: hlsProps.hlsResumeTime !== undefined ? hlsProps.hlsResumeTime : state.videoState.hlsResumeTime
            }
          }));
        }
      }, 50);
    }
    
    return;
  }
  // Handle other update sources
  // console.log(`[AppStore ${timestamp}] 📝 updateVideoState调用 来源:${source}, 更新:`, updates);
  
  // If already processing an update, filter minor updates
  if (metadata.processingUpdate && source !== 'internal' && source !== 'eventNavigation') {
    // Check for stuck processing state
    if (metadata.lastUpdateTime && Date.now() - metadata.lastUpdateTime > 5000) {
      console.warn(`[AppStore ${timestamp}] ⚠️ 检测到卡住的处理状态，强制清除`);
      // Force clear stuck state
      set(state => ({
        metadata: {
          ...state.metadata,
          processingUpdate: false
        }
      }));
    } else {
      // Check if this is an important update
      const isTimeUpdate = updates.time !== undefined;
      const isVideoUrlUpdate = updates.videoUrl !== undefined;
      const isRobotUpdate = updates.robotId !== undefined;
      
      // Video URL or robot updates always processed
      if (isVideoUrlUpdate || isRobotUpdate) {
        console.log(`[AppStore ${timestamp}] 🚀 处理重要更新，尽管处理中:`, updates);
        // Continue processing
      } else if (isTimeUpdate) {
        // For time updates, check if significantly different
        const currentTime = currentState.videoState.currentTime;
        if (Math.abs(updates.time - currentTime) > 0.5) {
          console.log(`[AppStore ${timestamp}] ⏱️ 处理重要时间更新:`, updates);
          // Continue processing
        } else {
          // Skip minor time updates
          return;
        }
      } else {
        // Skip other minor updates
        return;
      }
    }
  }
  
  // Check if switching videos
  const isVideoSwitch = 
    (updates.videoUrl && updates.videoUrl !== currentState.videoUrl) || 
    (updates.robotId && updates.robotId !== currentState.selection.activeRobotId);
  
  // If switching videos, handle specially
  if (isVideoSwitch) {
    window.isVideoChanging = true;
    console.log(`[AppStore ${timestamp}] 🔄 检测到视频切换`);
    
    // 检查是否是HLS视频
    const isHls = updates.videoUrl && updates.videoUrl.includes('.m3u8');
    
    // 计算实际时间 - 修复：确保我们保留当前时间位置
    const timeToPreserve = updates.time !== undefined ? updates.time : 
                       (isHls ? get().videoState.currentTime : get().videoState.hlsResumeTime);
    
    // 快速更新状态
    set(state => ({
      videoState: {
        ...state.videoState,
        currentTime: timeToPreserve, // 使用保留的时间
        time: timeToPreserve,        // 使用保留的时间
        isPlaying: true,
        lastVideoSwitch: Date.now(),
        isHls,
        hlsResumeTime: updates.hlsResumeTime,
        hlsBaseTime: isHls ? timeToPreserve : 0 // 如果是HLS视频，使用保留的时间作为基准
      },
      metadata: {
        ...state.metadata,
        processingUpdate: true,
        updateSource: source,
        lastUpdateTime: Date.now(),
      }
    }));
    
    // 立即更新其他状态
    set(state => {
      const newState = { ...state };
      
      if (updates.videoUrl) {
        newState.videoUrl = updates.videoUrl;
      }
      
      if (updates.robotId) {
        newState.selection = {
          ...state.selection,
          activeRobotId: updates.robotId,
        };
      }
      
      if (updates.eventId !== undefined) {
        newState.selection = {
          ...newState.selection, 
          activeEventId: updates.eventId,
        };
      }
      
      return newState;
    });
    
    // 使用 RAF 确保视觉更新的流畅性
    requestAnimationFrame(() => {
      set(state => ({
        videoState: {
          ...state.videoState,
          isPlaying: true,
          lastVideoSwitch: null,
          currentTime: timeToPreserve, // 确保在动画帧中保留时间
          time: timeToPreserve         // 确保在动画帧中保留时间
        },
        metadata: {
          ...state.metadata,
          processingUpdate: false,
          updateSource: null,
        }
      }));
      
      window.isVideoChanging = false;
    });
    
    return;
  }
  
  // Regular update (non-video switch) - use single state update
  set(state => {
    // Prepare new state object
    const newState = { ...state };
    
    // Update video state
    if (Object.keys(updates).some(key => ['time', 'isPlaying', 'duration', 'isBuffering', 'playbackRate', 'volume'].includes(key))) {
      newState.videoState = { ...state.videoState };
      
      if (updates.time !== undefined) {
        newState.videoState.currentTime = updates.time;
      
        // ✅ 只有在 timeline_event_click 时才允许设置偏移时间
        // 在 appStore.js 中，修改 updateVideoState 函数的 timeline_event_click 部分

if (source === 'timeline_event_click') {
  // 现有代码...
  
  // 在 return 语句前添加以下代码：
  // 同时高亮时间轴和故事章节（如果存在）
  setTimeout(() => {
    // 尝试查找并高亮故事章节
    const storyChapter = document.querySelector(`[data-event-id="${updates.eventId}"]`);
    if (storyChapter) {
      storyChapter.classList.add('story-highlight');
      storyChapter.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      
      setTimeout(() => {
        storyChapter.classList.remove('story-highlight');
      }, 2000);
    }
    
    // 尝试查找并高亮时间轴事件
    const timelineEvent = document.getElementById(`timeline-event-robot${updates.robotId}-${updates.eventId}`);
    if (timelineEvent) {
      document.querySelectorAll('.timeline-event').forEach(el => {
        el.classList.remove('active');
      });
      
      timelineEvent.classList.add('active');
    }
  }, 100);
  
  return;
}
      }
      
      if (updates.isPlaying !== undefined) {
        console.log(`[AppStore ${timestamp}] ${updates.isPlaying ? '▶️' : '⏸️'} 设置播放状态:`, updates.isPlaying);
        newState.videoState.isPlaying = updates.isPlaying;
      }
      
      if (updates.duration !== undefined) newState.videoState.duration = updates.duration;
      if (updates.isBuffering !== undefined) newState.videoState.isBuffering = updates.isBuffering;
      if (updates.playbackRate !== undefined) newState.videoState.playbackRate = updates.playbackRate;
      if (updates.volume !== undefined) newState.videoState.volume = updates.volume;
      
      // Important: Always handle lastVideoSwitch flag
      if (updates.lastVideoSwitch !== undefined) {
        newState.videoState.lastVideoSwitch = updates.lastVideoSwitch;
      } else if (source === 'videosync_cleanup' || source === 'videoPlayer_user_pause') {
        // Explicitly clear switch flag for certain sources
        newState.videoState.lastVideoSwitch = null;
      }
    }
    
    // 添加HLS相关属性处理
    if (updates.isHls !== undefined) {
      newState.videoState = { ...newState.videoState, isHls: updates.isHls };
      console.log(`[AppStore ${timestamp}] 🎬 设置 isHls: ${updates.isHls}`);
    }
    
    if (updates.hlsResumeTime !== undefined) {
      newState.videoState = { ...newState.videoState, hlsResumeTime: updates.hlsResumeTime };
      console.log(`[AppStore ${timestamp}] ⏱️ 设置 hlsResumeTime: ${updates.hlsResumeTime}`);
    }
    
    if (updates.hlsBaseTime !== undefined) {
      newState.videoState = { ...newState.videoState, hlsBaseTime: updates.hlsBaseTime };
      console.log(`[AppStore ${timestamp}] 🏁 设置 hlsBaseTime: ${updates.hlsBaseTime}`);
    }
    
    if (updates.currentEvent !== undefined) {
      newState.videoState = { ...newState.videoState, currentEvent: updates.currentEvent };
    }
    
    // Update selection state
    if (updates.robotId !== undefined || updates.eventId !== undefined) {
      newState.selection = { ...state.selection };
      
      if (updates.robotId !== undefined) {
        console.log(`[AppStore ${timestamp}] 🤖 更新活跃机器人ID:`, updates.robotId);
        newState.selection.activeRobotId = updates.robotId;
      }
      
      if (updates.eventId !== undefined) {
        console.log(`[AppStore ${timestamp}] 📌 更新活跃事件ID:`, updates.eventId);
        newState.selection.activeEventId = updates.eventId;
      }
    }
    
    // Update video URL
    if (updates.videoUrl !== undefined) {
      console.log(`[AppStore ${timestamp}] 🎬 更新视频URL:`, updates.videoUrl);
      newState.videoUrl = updates.videoUrl;
    }
    
    // Update timeframe
    if (updates.timeframe !== undefined) {
      newState.timeframe = updates.timeframe;
    }
    
    // Update metadata
    newState.metadata = {
      lastUpdate: Date.now(),
      updateSource: source,
      processingUpdate: false,
    };
    
    return newState;
  });
},
// Action: Change timeframe
changeTimeframe: (newTimeframe) => {
  // console.log(`[时间:${new Date().toISOString()}] 🕒 更改时间框架到:`, newTimeframe);
  const currentState = get();
  
  // Update timeframe
  set(state => ({
    timeframe: newTimeframe,
    metadata: {
      ...state.metadata,
      lastUpdate: Date.now(),
    }
  }));
  
  // If there's currently an active video URL, update URL to match new timeframe
  if (currentState.videoUrl) {
    const timePart = newTimeframe === "11AM" ? "day" : "night";
    const oppositePart = timePart === "day" ? "night" : "day";
    
    if (currentState.videoUrl.includes(`/${oppositePart}/`)) {
      const newUrl = currentState.videoUrl.replace(`/${oppositePart}/`, `/${timePart}/`);
      console.log(`[时间:${new Date().toISOString()}] 🎬 更新视频URL以匹配新时间框架:`, newUrl);
      
      // Use updateVideoState to execute URL update
      get().updateVideoState({ 
        videoUrl: newUrl, 
        timeframe: newTimeframe 
      }, 'changeTimeframe');
    }
  }
},

// Action: Update selected videos
updateSelectedVideos: (videoId, isSelected) => {
  console.log(`[时间:${new Date().toISOString()}] 🎥 更新所选视频 ${videoId}:`, isSelected);
  set(state => ({
    selection: {
      ...state.selection,
      selectedVideos: {
        ...state.selection.selectedVideos,
        [videoId]: isSelected,
      }
    }
  }));
},

// Action: Initialize selected videos
initializeSelectedVideos: (videoIds) => {
  console.log(`[时间:${new Date().toISOString()}] 🎥 初始化所选视频:`, videoIds);
  const selectedVideos = {};
  videoIds.forEach(id => {
    selectedVideos[id] = true;
  });
  
  set(state => ({
    selection: {
      ...state.selection,
      selectedVideos
    }
  }));
},

// Action: Add tracked object
addTrackedObject: (trackData) => {
  // Determine object category based on label
  let category = 'event'; // Default category
  const label = trackData.label?.toLowerCase() || '';
  
  if (label.includes('people') || label.includes('person')) {
    category = 'person';
  } else if (label.includes('car') || label.includes('vehicle') || label.includes('truck') || label.includes('sedan')) {
    category = 'vehicle'; 
  }
  
  console.log(`[时间:${new Date().toISOString()}] 🔍 添加跟踪对象 类别:${category}`, trackData);
  
  set(state => {
    // Check if object is already in the tracking list
    const isAlreadyTracked = state.trackedObjects[category].some(item => 
      item.eventId === trackData.eventId && item.boxId === trackData.boxId
    );
    
    // If already exists and isTracking is false, remove
    if (!trackData.isTracking) {
      return {
        trackedObjects: {
          ...state.trackedObjects,
          [category]: state.trackedObjects[category].filter(item => 
            !(item.eventId === trackData.eventId && item.boxId === trackData.boxId)
          )
        }
      };
    }
    // If new object and isTracking is true, add
    else if (!isAlreadyTracked && trackData.isTracking) {
      return {
        trackedObjects: {
          ...state.trackedObjects,
          [category]: [...state.trackedObjects[category], trackData]
        }
      };
    }
    
    // Other cases remain unchanged
    return state;
  });
},

// Action: Video seek
seekTo: (time, source = null) => {
  const { updateVideoState } = get();
  // 添加时间范围检查
  const MAX_VIDEO_DURATION = 1800; // 30分钟
  const normalizedTime = Math.min(Math.max(0, time), MAX_VIDEO_DURATION);
  
  console.log(`[时间:${new Date().toISOString()}] ⏩ seekTo调用 时间:${normalizedTime}, 来源:${source}`);
  
  // 只在明确的事件点击时才跳转到事件起始点
  if (source === 'timeline_event_click' || source === 'eventNavigation') {
    // 1. Record the seek operation and pause
    updateVideoState({ 
      isPlaying: false,
      time: normalizedTime
    }, source);
    
    // 2. Play immediately without waiting
    updateVideoState({ 
      isPlaying: true 
    }, 'internal');
    console.log(`[时间:${new Date().toISOString()}] ▶️ 事件点击后立即自动播放`);
    return;
  }
  
  // 对于其他来源的跳转，保持当前播放状态
  updateVideoState({ 
    time: normalizedTime
  }, source || 'seekTo');
},

// Selector: Get current active robot's video URL
getCurrentRobotVideoUrl: () => {
  const state = get();
  const robotId = state.selection.activeRobotId;
  if (!robotId) return "";
  
  const timePart = state.timeframe === "11AM" ? "day" : "night";
  return `/${timePart}/videos/video${robotId}.mp4`;
}
}));